const { logLevels, stringify } = require('../helpers');
const Sentry = require('@sentry/browser');
const { makeOfflineTransport } = require('@sentry/core');
const createOfflineStore = require('./sentryOfflineStore');
let activeClient;
const patchedLoggers = new WeakSet();
let retireTransport = () => {};
let queueCleanup = Promise.resolve();
/**
 * @typedef {import('../helpers')} LogLevel
 */

/**
 *
 * @param {object[]} message(s)
 * @param {object} [userContext] Required when environment variable LOG_CONTEXT_INFO = true
 * @param {string} [userContext.tenantId]
 * @param {object} [userContext.activeTenant]
 * @param {string} [userContext.appId]
 * @param {object} [userContext.appContext]
 * @param {string} [userContext.userId]
 * @param {string} [userContext.userName]
 * @param {LogLevel} logLevel Level to log message with
 */
function output(message, userContext, logLevel) {
    if (message[0] instanceof Error) {
        message[0] = message[0].stack;
    }
    if (message.length === 1) {
        message = message[0];
    } else {
        message = stringify([message]);
    }

    const needStringify = typeof message === 'object';
    const messageOut = needStringify ? JSON.stringify(message) : message;
    switch (logLevel) {
        case logLevels.ERROR:
            Sentry.captureException(messageOut);
            break;
        case logLevels.WARN:
            Sentry.captureMessage(messageOut, 'warning');
            break;
        case logLevels.INFO:
            Sentry.captureMessage(messageOut, 'info');
            break;
        case logLevels.DEBUG:
            Sentry.captureMessage(messageOut, 'debug');
            break;
    }
}

/**
 * Use parameters from the logger to set up sentry.
 * In case the app does not have everything at the beginning, monkey-patch
 * the setters to intercept changes and update Sentry.
 * @param {any} logger index.js for this module
 */
function init(logger) {
    if (logger.environment !== 'prod') return;
    const { release, environment, userId, tenantId, useCaseTitle, sampleRate = 1, ignoredErrors } = logger;
    let active = true;
    retireTransport = () => { active = false; };
    // BrowserSession's history hook survives close and may have created an excluded session.
    Sentry.getCurrentScope().setSession();
    Sentry.getIsolationScope().setSession();
    Sentry.init({
        release,
        environment,
        dsn: 'https://cb7321c57c084fe591afdc88a6df4c8e@sentry.io/2831296',
        transport(options) {
            const offline = makeOfflineTransport(fetchOptions => {
                const fetchTransport = Sentry.makeFetchTransport(fetchOptions);
                return {
                    send: envelope => active ? fetchTransport.send(envelope) : Promise.resolve({}),
                    flush: timeout => active ? fetchTransport.flush(timeout) : Promise.resolve(true)
                };
            })({ ...options,
                createStore: storeOptions => createOfflineStore(() => active, () => queueCleanup, storeOptions),
                shouldStore: () => active
            });
            const onOnline = () => { if (active) void offline.flush(); };
            globalThis.addEventListener?.('online', onOnline);
            retireTransport = () => {
                active = false;
                globalThis.removeEventListener?.('online', onOnline);
            };
            return {
                send: envelope => active ? offline.send(envelope) : Promise.resolve({}),
                flush: timeout => active ? offline.flush(timeout) : Promise.resolve(true)
            };
        },
        transportOptions: {
            /**
             * Maximum number of events to store
             * Default: 30
             */
            maxQueueSize: 30
        },
        // percentage of errors to send to sentry
        sampleRate,
        // filter out "errors" that are actually user errors
        beforeSend: (event, hint) => active ? beforeSend(event, hint) : null,
        beforeBreadcrumb: crumb => active ? filterBreadCrumbs(ignoredErrors)(crumb) : null,
        ignoreErrors: ignoredErrors
    });
    activeClient = Sentry.getClient();
    // setupOnce starts only the first browser session. Re-entry needs a fresh permitted one.
    if (activeClient?.getIntegrationByName('BrowserSession') && !Sentry.getCurrentScope().getSession()) {
        Sentry.startSession({ ignoreDuration: true });
        Sentry.captureSession();
    }
    if (userId) {
        Sentry.setUser({ id: userId });
    }

    if (tenantId) {
        Sentry.setTag('tenantId', tenantId);
    }
    if (useCaseTitle) {
        Sentry.setTag('useCaseTitle', useCaseTitle);
    }

    if (patchedLoggers.has(logger)) return;
    patchedLoggers.add(logger);
    // Monkey patch the setters.
    const tempUserId = logger.setUserId.bind(logger);
    logger.setUserId = function (userId) {
        tempUserId(userId);
        if (logger.environment === 'prod') Sentry.setUser({ id: userId });
    };
    const tempTenantId = logger.setTenantId.bind(logger);
    logger.setTenantId = function (tenantId) {
        tempTenantId(tenantId);
        if (logger.environment === 'prod') Sentry.setTag('tenantId', tenantId);
    };

    const tempUseCase = logger.setUseCaseTitle.bind(logger);
    logger.setUseCaseTitle = function (useCaseTitle) {
        tempUseCase(useCaseTitle);
        if (logger.environment === 'prod') Sentry.setTag('useCaseTitle', useCaseTitle);
    };
}

module.exports = {
    output,
    init,
    stop
};

function stop() {
    retireTransport();
    Sentry.getCurrentScope().clearBreadcrumbs();
    Sentry.getIsolationScope().clearBreadcrumbs();
    if (activeClient) {
        activeClient.getOptions().enabled = false;
        // Retire transport first: close must never flush an excluded environment.
        void activeClient.close(0);
        activeClient = undefined;
    }
    Sentry.getCurrentScope().setSession();
    Sentry.getIsolationScope().setSession();
    module.exports.hasBeenInitialized = false;
    queueCleanup = Promise.all([
        queueCleanup, clearOfflineQueue('sentry-offline'), clearOfflineQueue('sentry-production-only')
    ]).then(() => {});
}

function clearOfflineQueue(name) {
    if (typeof indexedDB === 'undefined') return;
    const databaseFactory = indexedDB;
    try {
        return new Promise(resolve => {
            const request = databaseFactory.open(name);
            let absent = false;
            // No database existed, so there is nothing to discard. Do not create one during shutdown.
            request.onupgradeneeded = () => {
                absent = true;
                request.result.close();
                request.transaction.abort();
            };
            request.onerror = () => {
                if (!absent) { resolve(); return; }
                // Finish removing an aborted empty database before production opens its queue.
                try {
                    const deletion = databaseFactory.deleteDatabase(name);
                    deletion.onsuccess = deletion.onerror = () => resolve();
                } catch { resolve(); }
            };
            request.onsuccess = () => {
                const db = request.result;
                if (db.objectStoreNames.contains('queue')) {
                    const transaction = db.transaction('queue', 'readwrite');
                    transaction.objectStore('queue').clear();
                    transaction.oncomplete = transaction.onerror = transaction.onabort = () => { db.close(); resolve(); };
                } else {
                    db.close();
                    resolve();
                }
            };
        }).catch(() => {});
    } catch {
        // Private browsing can deny IndexedDB. The retired transport still blocks sends and retries.
    }
}

function beforeSend(event, hint) {
    const error = hint.originalException;
    if (error && error._isUserError) {
        return null;
    }
    return event;
}

function filterBreadCrumbs(ignoredErrors) {
    return function (crumb) {
        // these are exceptions logged as errors. We don't need the sentry event
        if (crumb.category === 'sentry' && ignoredErrors.includes(crumb.message)) {
            return null;
        }

        return crumb;
    };
}
